import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTour } from "@/context/TourContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { toast } from "sonner";
import { Rocket, FileText, Receipt, Lock, Archive, House, Download, Mail, Loader2, Lightbulb, ShieldCheck, LifeBuoy, AlertTriangle, Play, RotateCcw, Check } from "lucide-react";
import { cn } from "@/lib/utils";

const ICONS = { rocket: Rocket, "file-text": FileText, receipt: Receipt, lock: Lock, archive: Archive, house: House };

export default function HelpPage() {
  const { user } = useAuth();
  const tour = useTour();
  const location = useLocation();
  const [guide, setGuide] = useState(null);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const initial = useMemo(() => (location.hash || "").replace("#", "") || "start", [location.hash]);
  const [open, setOpen] = useState([initial]);

  const load = () => api.get("/guide").then((r) => { setGuide(r.data); setError(""); }).catch((e) => setError(errorMessage(e, "Kunne ikke laste veiledningen")));
  useEffect(() => { load(); }, []);
  useEffect(() => { if (initial) { setOpen((o) => (o.includes(initial) ? o : [...o, initial])); setTimeout(() => document.getElementById(`hjelp-${initial}`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 150); } }, [initial]);

  const download = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/guide/pdf", { responseType: "blob", timeout: 60000 });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "Kom-i-gang-med-Controlle.pdf"; a.rel = "noopener";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      toast.success("Veiledningen er lastet ned som PDF");
    } catch (e) {
      toast.error(errorMessage(e, "Kunne ikke lage PDF"));
    } finally {
      setDownloading(false);
    }
  };

  const sendMail = async () => {
    setSending(true);
    try {
      const r = await api.post("/guide/send", null, { timeout: 60000 });
      toast.success(`Veiledningen er sendt til ${r.data.to}`, { description: "Sjekk gjerne søppelpost hvis den ikke dukker opp innen et par minutter." });
    } catch (e) {
      const status = e?.response?.status;
      (status === 429 ? toast.info : toast.error)(errorMessage(e, "Kunne ikke sende e-posten"));
    } finally {
      setSending(false);
    }
  };

  const sections = guide?.sections || [];

  return (
    <div className="space-y-6" data-testid="help-page">
      <PageHeader
        title="Hjelp og brukerveiledning"
        description="En enkel veiledning til Regninger, Faktura og Passord – samme innhold som du fikk på e-post da du opprettet kontoen."
        actions={(
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={download} disabled={downloading || !guide} data-testid="help-download-pdf">{downloading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />} Last ned PDF</Button>
            <Button onClick={sendMail} disabled={sending || !guide} data-testid="help-send-email">{sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />} Send meg veiledningen på e-post</Button>
          </div>
        )}
      />

      {error ? (
        <Alert className="status-forfalt border" data-testid="help-error">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Kunne ikke laste veiledningen</AlertTitle>
          <AlertDescription className="flex items-center justify-between gap-3">{error}<Button size="sm" variant="outline" onClick={load} data-testid="help-retry">Prøv igjen</Button></AlertDescription>
        </Alert>
      ) : null}

      {!guide && !error ? (
        <div className="space-y-3" data-testid="help-loading">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-16" />)}</div>
      ) : null}

      {guide ? (
        <>
          {/* Hurtignavigasjon */}
          <nav aria-label="Gå til modul" className="flex flex-wrap gap-2" data-testid="help-quicknav">
            {sections.map((s) => {
              const Icon = ICONS[s.icon] || LifeBuoy;
              return (
                <Button key={s.key} variant="outline" size="sm" className={cn("rounded-full", open.includes(s.key) && "bg-accent text-accent-foreground border-primary/40")} onClick={() => { setOpen((o) => (o.includes(s.key) ? o : [...o, s.key])); setTimeout(() => document.getElementById(`hjelp-${s.key}`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 250); }} data-testid={`help-jump-${s.key}`}>
                  <Icon className="h-3.5 w-3.5 mr-1.5" /> {s.short || s.title}
                </Button>
              );
            })}
          </nav>

          <Accordion type="multiple" value={open} onValueChange={setOpen} className="space-y-3">
            {sections.map((s, idx) => {
              const Icon = ICONS[s.icon] || LifeBuoy;
              return (
                <AccordionItem key={s.key} value={s.key} id={`hjelp-${s.key}`} className="rounded-xl border bg-card px-5 scroll-mt-24" data-testid={`help-section-${s.key}`}>
                  <AccordionTrigger className="hover:no-underline py-4">
                    <span className="flex items-center gap-3 text-left">
                      <span className="h-9 w-9 rounded-lg bg-accent text-accent-foreground flex items-center justify-center shrink-0"><Icon className="h-4 w-4" /></span>
                      <span>
                        <span className="block font-heading text-lg font-semibold leading-tight">{s.title}</span>
                        <span className="block text-xs text-muted-foreground font-normal">{s.steps.length} steg{idx === 0 ? " · start her" : ""}</span>
                      </span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-5">
                    <div className="flex flex-col sm:flex-row sm:items-start gap-3 mb-4">
                      <p className="text-sm text-muted-foreground max-w-3xl flex-1">{s.intro}</p>
                      <Button variant="outline" size="sm" className="shrink-0" onClick={() => tour.startTour(s.key, "manual")} data-testid={`help-play-${s.key}`}>
                        <Play className="h-4 w-4 mr-1.5" /> Se gjennomgang{tour.seen[s.key] ? <Check className="h-3.5 w-3.5 ml-1.5 text-success" aria-label="Sett" /> : null}
                      </Button>
                    </div>
                    <ol className="space-y-3" data-testid={`help-steps-${s.key}`}>
                      {s.steps.map((st, i) => (
                        <li key={i} className="flex gap-3">
                          <span className="h-7 w-7 rounded-full bg-primary text-primary-foreground text-xs font-semibold flex items-center justify-center shrink-0 mt-0.5 tnum">{i + 1}</span>
                          <div className="min-w-0">
                            <p className="font-medium leading-snug">{st.title}</p>
                            <p className="text-sm text-muted-foreground leading-relaxed">{st.text}</p>
                          </div>
                        </li>
                      ))}
                    </ol>
                    {s.tip ? (
                      <div className="mt-4 rounded-lg bg-accent/70 border border-primary/20 px-4 py-3 text-sm flex gap-2" data-testid={`help-tip-${s.key}`}>
                        <Lightbulb className="h-4 w-4 text-primary shrink-0 mt-0.5" /><span><span className="font-medium">Tips:</span> {s.tip}</span>
                      </div>
                    ) : null}
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>

          <Card>
            <CardContent className="p-5 flex flex-col sm:flex-row sm:items-center gap-4">
              <span className="h-10 w-10 rounded-lg bg-accent text-accent-foreground flex items-center justify-center shrink-0"><ShieldCheck className="h-5 w-5" /></span>
              <div className="flex-1 text-sm">
                <p className="font-medium">Dine data er kun dine</p>
                <p className="text-muted-foreground">Ingen andre brukere kan se regningene, fakturaene eller passordene dine – med mindre du selv gir fullmakt under Familie og fullmakt. Passordhvelvet krypteres på din enhet, og Controlle kan aldri lese innholdet.</p>
              </div>
              <p className="text-xs text-muted-foreground shrink-0">Veiledning v{guide.version}{user?.email ? ` · sendes til ${user.email}` : ""}</p>
            </CardContent>
          </Card>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl border bg-card px-5 py-4" data-testid="help-tours-card">
            <div className="text-sm">
              <p className="font-medium">Animerte gjennomganger</p>
              <p className="text-muted-foreground">Vises automatisk første gang du åpner en modul. {Object.keys(tour.seen).length ? `${Object.keys(tour.seen).length} av ${sections.length} er sett.` : "Ingen er sett ennå."}</p>
            </div>
            <Button variant="outline" size="sm" onClick={async () => { await tour.resetTours("alle"); toast.success("Gjennomgangene vises igjen neste gang du åpner modulene"); }} disabled={!Object.keys(tour.seen).length} data-testid="help-tours-reset">
              <RotateCcw className="h-4 w-4 mr-1.5" /> Vis gjennomgangene igjen
            </Button>
          </div>
        </>
      ) : null}
    </div>
  );
}
